import React, {useEffect, useRef, useState, useMemo} from "react";
import {Button, Flex, Input, InputGroup, Text} from "@chakra-ui/react";
import {CiCircleRemove} from "react-icons/ci";
import UploadIcon from "../../assets/upload.svg";
import {ALLOWED_TYPES} from "../../models/constants/constants.ts";
import {getFileBaseName} from "../../utils/utils.ts";
import {SearchSeriesResultDto} from "../../models/interfaces/dtos/searchSeriesResult.dto.interface.ts";
import AppSpinner from "../AppSpinner.tsx";
import SmartIcon from "../SmartIcon.tsx";
import {FaLayerGroup} from "react-icons/fa6";
import {seriesSearch} from "../../services/seriesService.ts";
import useUserStore, {IUserState} from "../../store/useUserStore.ts";
import {NewProjectConfigurations} from "../../models/interfaces/newProjectConfigurations.ts";
import Select from "../Select.tsx";
import {IItemsSelect} from "../../models/interfaces/selectItems.interface.ts";
import {FcDataConfiguration} from "react-icons/fc";

interface DragAndDropProps {
  uploadFiles: File[];
  customConfigurationId?: string;
  setUploadedFiles: (files: File[]) => void;
  fileNameToSeriesNameRelation: Record<string, string>;
  setFileNameToSeriesNameRelation: (relation: Record<string, string>) => void;
  setCustomConfiguration: (configId: string | undefined) => void;
  onVideoLoaded: (duration: number) => void;
  onVideoRemoved: (duration: number) => void;
  handleErrors: (error: string, action?: "add" | "remove") => void;
}

const DragAndDrop: React.FC<DragAndDropProps> = (
  {
    uploadFiles,
    customConfigurationId,
    setUploadedFiles,
    fileNameToSeriesNameRelation,
    setFileNameToSeriesNameRelation,
    setCustomConfiguration,
    onVideoLoaded,
    onVideoRemoved,
    handleErrors
  }: DragAndDropProps) => {
  const newProjectCreationConfigurations: NewProjectConfigurations = useUserStore((state: IUserState) => state.preferences?.newProjectCreation || {} as NewProjectConfigurations)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [hasSearchAttempted, setHasSearchAttempted] = useState(false);
  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedInput, setDebouncedInput] = useState<string>("");
  const [seriesOptions, setSeriesOptions] = useState<Record<string, string>>({});

  const creationConfigurationsItems: IItemsSelect[] = useMemo(() => {
    return Object.keys(newProjectCreationConfigurations).map((configId) => ({
      id: configId,
      title: newProjectCreationConfigurations[configId].name,
    }));
  }, [newProjectCreationConfigurations]);

  const customConfigurationItem: IItemsSelect | undefined = creationConfigurationsItems.find((item: IItemsSelect) => item.id === customConfigurationId);

  useEffect(() => {
    if (!searchTerm) {
      setDebouncedInput("");
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedInput(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const trimmed: string = debouncedInput.trim();
    if (trimmed.length < 3) {
      setHasSearchAttempted(false);
      setHasSearchCompleted(true);
      setSeriesOptions({});
      return;
    }
    setIsLoading(true);
    setHasSearchAttempted(true);
    setHasSearchCompleted(false);

    seriesSearch(trimmed)
      .then((res: SearchSeriesResultDto[]) => {
        const uniqueSeries: Record<string, string> = res.reduce((acc: Record<string, string>, series: SearchSeriesResultDto) => {
          if (!acc[series._id]) {
            acc[series._id] = series.name;
          }
          return acc;
        }, {});
        setSeriesOptions(uniqueSeries);
      })
      .finally(() => {
        setIsLoading(false);
        setHasSearchCompleted(true);
      });
  }, [debouncedInput]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | null = event.target.files;
    if (files && validateNumOfFiles(files)) {
      const filesArray: File[] = Array.from(files).slice(0, 10 - uploadFiles.length);
      batchValidateAndAddFiles(filesArray);
    }

    // ✅ Clear input so selecting the same file again will trigger `onChange`
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const files: FileList = event.dataTransfer.files;
    if (files && validateNumOfFiles(files)) {
      const filesArray: File[] = Array.from(files).slice(0, 10 - uploadFiles.length);
      batchValidateAndAddFiles(filesArray);
    }
  };

  const validateNumOfFiles = (files: FileList): boolean => {
    if (files.length >= 10) {
      handleErrors("You can only upload up to 10 files.");
      setTimeout(() => handleErrors("You can only upload up to 10 files.", "remove"), 3000)
      return false;
    }
    return true;
  }


  const batchValidateAndAddFiles = (filesArray: File[]) => {
    const validatedFiles: File[] = [];
    let processedCount: number = 0;

    filesArray.forEach((file: File) => {
      const fileType: string = file.type;

      if (!ALLOWED_TYPES[fileType]) {
        handleErrors("Only WMV, AVI, MP4, MKV, and MOV files are supported.");
        processedCount++;
        return;
      }
      handleErrors("Only WMV, AVI, MP4, MKV, and MOV files are supported.", "remove");

      const video: HTMLVideoElement = document.createElement("video");
      video.preload = "metadata";
      video.src = URL.createObjectURL(file);

      video.onloadedmetadata = () => {
        const durationInMinutes: number = video.duration / 60;
        onVideoLoaded(durationInMinutes);
        (file as any)._duration = durationInMinutes;

        const isDuplicate: boolean = uploadFiles.some((f: File) => f.name === file.name);
        if (!isDuplicate) {
          validatedFiles.push(file);
        }

        processedCount++;
        if (processedCount === filesArray.length) {
          const updated: File[] = [...uploadFiles, ...validatedFiles];
          setUploadedFiles(updated);
          if (updated.length === validatedFiles.length) {
            requestIdleCallback(() => setSelectedFileIndex(0));
          }
        }
      };
    });
  };


  const removeFile = (
    e: React.MouseEvent<SVGAElement, MouseEvent>,
    file: File,
    index: number,
  ) => {
    e.stopPropagation();
    if (fileNameToSeriesNameRelation[file.name]) {
      wireFileToSeriesName(file.name, fileNameToSeriesNameRelation[file.name]);
    }
    const updated: File[] = uploadFiles.filter((_: File, i: number) => i !== index);
    if (selectedFileIndex === index) {
      setSelectedFileIndex(updated.length ? Math.max(index - 1, 0) : null);
    }
    if ((file as any)._duration) {
      onVideoRemoved((file as any)._duration);
    }
    setUploadedFiles(updated);
  }

  const wireFileToSeriesName = (fileName: string, seriesName: string) => {
    const isSame: boolean = fileNameToSeriesNameRelation[fileName] === seriesName;
    const updated = {...fileNameToSeriesNameRelation};

    if (isSame) {
      delete updated[fileName];
    } else {
      updated[fileName] = seriesName;
    }
    setFileNameToSeriesNameRelation(updated);
    setSearchTerm("")

  };

  const setAllProjectSameSeries = () => {
    if (selectedFileIndex === null || uploadFiles.length === 0) return;

    const seriesName: string = fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name];
    if (!seriesName) return;

    const updatedFileNameToSeriesRelation: Record<string, string> = {};
    uploadFiles.forEach((file) => {
      updatedFileNameToSeriesRelation[file.name] = seriesName;
    });
    setFileNameToSeriesNameRelation(updatedFileNameToSeriesRelation);
  }

  return (
    <Flex className="text-center justify-center flex-col items-center">
      {uploadFiles.length ? (
        <Flex className="flex-col w-[700px] h-[350px] gap-4">
          <Flex wrap="wrap" gap="2">
            {uploadFiles.map((file, index) => (
              <Flex
                key={index}
                align="center"
                px="3"
                py="1"
                bg={selectedFileIndex === index ? "purple.600" : "gray.600"}
                borderRadius="lg"
                cursor="pointer"
                onClick={() => setSelectedFileIndex(index)}
                _hover={{bg: "purple.500"}}
                gap="2"
              >
                <Text color="white" fontSize="sm">
                  {getFileBaseName(file.name)}
                </Text>
                <CiCircleRemove
                  size={18}
                  className="cursor-pointer text-gray-300 hover:text-red-400"
                  onClick={(e: React.MouseEvent<SVGAElement, MouseEvent>) => removeFile(e, file, index)}
                />
              </Flex>
            ))}
          </Flex>

          {selectedFileIndex !== null && (
            <>
              <InputGroup
                className="w-full h-[52px] border border-transparent bg-transparent rounded-lg flex items-center group focus-within:border-brandPurple-500 focus-within:ring-2 focus-within:ring-brandPurple-500 relative"
              >
                {/* Left Icon */}
                <img src={UploadIcon} className="max-w-[22px] ml-2" alt="upload-file"/>

                {/* File Name Input */}
                <Input
                  autoFocus
                  type="text"
                  placeholder="Project Name"
                  value={getFileBaseName(uploadFiles[selectedFileIndex].name)}
                  onChange={(e) => {
                    const newName = e.target.value;
                    const ext: string | undefined = uploadFiles[selectedFileIndex].name.split(".").pop();

                    const nameExists: boolean = uploadFiles.some((f, i) =>
                      i !== selectedFileIndex && getFileBaseName(f.name) === newName
                    );
                    if (nameExists) return;

                    const updatedFile = new File(
                      [uploadFiles[selectedFileIndex]],
                      `${newName}.${ext}`,
                      {type: uploadFiles[selectedFileIndex].type}
                    );

                    const updatedFileNameToSeriesRelation = {...fileNameToSeriesNameRelation};
                    if (fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name]) {
                      updatedFileNameToSeriesRelation[`${newName}.${ext}`] = fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name];
                      delete updatedFileNameToSeriesRelation[uploadFiles[selectedFileIndex].name];
                      setFileNameToSeriesNameRelation(updatedFileNameToSeriesRelation);
                    }

                    setUploadedFiles(uploadFiles.map((f: File, i: number) => (i === selectedFileIndex ? updatedFile : f)));
                  }}
                  sx={{
                    boxShadow: "none !important",
                    borderColor: "transparent !important",
                    outline: "none !important",
                    _focus: {
                      boxShadow: "none !important",
                      borderColor: "transparent !important",
                      outline: "none !important",
                    },
                    _active: {
                      boxShadow: "none !important",
                      borderColor: "transparent !important",
                      outline: "none !important",
                    },
                    _hover: {
                      boxShadow: "none !important",
                      borderColor: "transparent !important",
                      outline: "none !important",
                    }
                  }}
                  className="w-full text-loginInputsText-500 bg-transparent border-none outline-none focus:ring-0 focus:border-transparent rounded-lg pl-12 py-2 peer"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  position="absolute"
                  right="2"
                  top="50%"
                  transform="translateY(-50%)"
                  bg="transparent"
                  zIndex={10}
                  _hover={{bg: "gray.700"}}
                  size="md"
                  p={1}
                >
                  <Text fontSize="3xl" color="white">+</Text>
                </Button>
              </InputGroup>
              <div className="flex flex-col items-start justify-center">
                <div className="w-full flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    Series
                    {fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name] && (
                      <Flex
                        align="center"
                        px="3"
                        py="1"
                        bg="purple.600"
                        borderRadius="lg"
                        cursor="pointer"
                        _hover={{bg: "purple.500"}}
                        gap="2"
                      >
                        <div className="flex items-center gap-2 text-white text-sm">
                          {fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name]}
                          {Object.keys(fileNameToSeriesNameRelation).length > 1 && Object.keys(fileNameToSeriesNameRelation).length == uploadFiles.length
                            && Object.values(fileNameToSeriesNameRelation).every((seriesName: string) => seriesName === Object.values(fileNameToSeriesNameRelation)[0])
                            && <SmartIcon name={FaLayerGroup} className="h-3 w-3"/>
                          }
                        </div>
                        <CiCircleRemove
                          size={18}
                          className="cursor-pointer text-gray-300 hover:text-red-400"
                          onClick={() => wireFileToSeriesName(uploadFiles[selectedFileIndex].name, fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name])}
                        />
                      </Flex>
                    )}
                  </div>
                  {Object.keys(fileNameToSeriesNameRelation).length > 0 && uploadFiles.length > 1 && <div
                      className="cursor-pointer hover:text-brandPurple-500 active:scale-110 transition-transform"
                      onClick={setAllProjectSameSeries}
                  >
                      Include all videos
                  </div>}
                </div>
                <span className="text-sm text-n-400 mt-1">Choose series from the list or create new one</span>
                <input
                  type="text"
                  placeholder="Series Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-loginInputsText-500 bg-transparent rounded-lg p-2 border border-brandPurple-500/50 hover:border-brandPurple-500 focus:border-brandPurple-500 focus:outline-none appearance-none mt-2"
                />
                <div className="flex flex-col items-start w-full max-h-[150px] overflow-auto ml-1 mt-2 pr-1 gap-2">
                  {isLoading ? <AppSpinner className="w-6 h-6"/> : null}
                  {Object.keys(seriesOptions).map((seriesId: string) => (
                    <div
                      key={seriesId}
                      className="flex w-full p-2 rounded-md hover:bg-gray-700"
                      onClick={() => wireFileToSeriesName(uploadFiles[selectedFileIndex].name, seriesOptions[seriesId])}
                    >
                      {seriesOptions[seriesId]}
                    </div>

                  ))}
                  {debouncedInput.trim().length > 0 && debouncedInput.trim().length < 3 && (
                    <div className="flex w-full p-2 rounded-md text-sm text-yellow-300">
                      Please enter at least 3 characters to search.
                    </div>
                  )}
                  {searchTerm.trim().length >= 3 &&
                    hasSearchAttempted &&
                    hasSearchCompleted &&
                    Object.keys(seriesOptions).length === 0 &&
                    !isLoading && (
                      <div
                        className="flex items-center justify-between w-full p-2 rounded-md hover:bg-gray-700 cursor-pointer"
                        onClick={() => wireFileToSeriesName(uploadFiles[selectedFileIndex].name, searchTerm)}
                      >
                        <div className="flex gap-2">
                          {fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name] == searchTerm && (
                            <div><SmartIcon name={"check"} className="w-4 h-4" fill="#fff"/></div>
                          )}
                          {searchTerm}
                        </div>
                        <div>Add new series</div>
                      </div>
                    )}
                </div>
              </div>
              <div className="flex flex-col items-start justify-center">
                <Select
                  className="w-full"
                  label="Configuration"
                  classButton="h-13 rounded-xl shadow-[inset_0_0_0_0.0625rem_#DADBDC] caption1 dark:shadow-[inset_0_0_0_0.0625rem_#2A2E2F] dark:bg-transparent"
                  classOptions="min-w-full overflow-auto max-h-40"
                  classIcon="w-5 h-5 fill-n-400/50"
                  classArrow="fill-n-400"
                  icon={FcDataConfiguration}
                  placeholder="Select Configuration"
                  items={creationConfigurationsItems}
                  value={customConfigurationItem ?? null}
                  onChange={(selectedItem: IItemsSelect) => customConfigurationItem?.id == selectedItem.id ? setCustomConfiguration(undefined) : setCustomConfiguration(selectedItem.id)}
                />
              </div>
            </>
          )}
        </Flex>
      ) : (
        <Flex
          className={`flex-col w-[700px] h-[400px] border-brandPurple-500 border ${dragging ? 'border-solid' : 'border-dashed'} rounded-xl ${dragging ? 'bg-n-500' : 'bg-n-600'} gap-4 justify-center items-center p-2 cursor-pointer transition-all`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <img src={UploadIcon} className="max-w-[42px]" alt="upload-file"/>
          <Flex className="flex-col gap-1 items-center justify-center">
            <Text className="text-lg text-white">Drag your file to upload</Text>
          </Flex>
          <Flex className="items-center justify-center gap-4">
            <div className="w-[100px] h-[1px] bg-loginInputsText-500"></div>
            <Text className="text-loginInputsText-500">OR</Text>
            <div className="w-[100px] h-[1px] bg-loginInputsText-500"></div>
          </Flex>
          <Flex className="justify-center">
            <button className="border border-white rounded-lg py-2 px-4 font-semibold text-white">
              Browse files
            </button>
          </Flex>
        </Flex>
      )}
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileSelect}
        accept="video/*"
        multiple // <-- allow multiple files
      />
    </Flex>
  );
};

export default DragAndDrop;